import CommonTable from "components/ReactTable/Table";
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import Select from "react-select";
import {
  fixedWidthCustomStyle,
  JOB_STATUS_OPTIONS,
  DEFAULT_RECORDS_PER_PAGE,
  INTERNAL_MONITORING_STATUS,
  SMALL_CASE_WORK_ORDER,
  CAP_CASE_WORK_ORDER,
  MAX_MANUAL_JOB_IDS,
} from "components/Common/constants";
import {
  formatDateLocal,
  ISOToHumanReadableDate,
} from "components/Common/commonFunctions";
import { useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector, useDispatch } from "react-redux";
import {
  getJobList,
  exportJobsCSV,
  downloadBulkWorkOrderReport,
  downloadBulkWorkOrderReportList,
  downloadBulkWorkOrderReportsStatus,
  downloadBulkWorkOrderReportsdownloadByBrowser,
  downloadWorkOrderQuotationItemsCSV,
} from "actions/job";
import { jobList } from "constants/job";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";
import { getRegionList, getTechniciansList } from "actions/technicians";
import { getPaymentTermList, getCustomerList } from "actions/customer";
import { getJobTypeList } from "actions/jobType";
import { regionList, techniciansList } from "constants/technicians";
import { paymentTermList, customerList } from "constants/customer";
import { jobTypeList } from "constants/jobType";
import { WORK_ORDER,  } from "components/Common/constants";
import moment from "moment";
import NotificationAlert from "react-notification-alert";
import { QUOTATION_STATUS } from "components/Common/constants";
import { showAlert } from "components/Common/commonFunctions";
import { colors } from "@mui/material";

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════
const unwrapExportPayload = (res) => {
  if (!res || typeof res !== "object") return {};
  if (
    res.data &&
    typeof res.data === "object" &&
    (res.data.exportID || res.data.status)
  ) {
    return res.data;
  }
  if (res.exportID || res.status) return res;
  return res.data || {};
};

const extractExportsArray = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data?.data?.exports)) return raw.data.data.exports;
  if (Array.isArray(raw?.data?.data)) return raw.data.data;
  if (Array.isArray(raw?.data?.exports)) return raw.data.exports;
  if (Array.isArray(raw?.exports)) return raw.exports;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
};

/**
 * Map backend status string → UI status
 *   Terminal success:  COMPLETED | PROCESSED | SUCCESS | DONE | FINISHED
 *   Terminal failure:  FAILED | ERROR | CANCELLED
 *   Active:            PENDING | QUEUED | IN_PROGRESS | PROCESSING | RUNNING
 */
const TERMINAL_SUCCESS = [
  "COMPLETED",
  "PROCESSED",
  "SUCCESS",
  "DONE",
  "FINISHED",
];
const TERMINAL_FAILURE = ["FAILED", "ERROR", "CANCELLED"];

const mapBackendStatus = (raw, { successful = 0, failed = 0 } = {}) => {
  const s = String(raw || "").toUpperCase();
  if (TERMINAL_SUCCESS.includes(s)) {
    if (failed > 0 && successful === 0) return "failed";
    return "ready";
  }
  if (TERMINAL_FAILURE.includes(s)) return "failed";
  return "downloading";
};

const isTerminalStatus = (raw) => {
  const s = String(raw || "").toUpperCase();
  return TERMINAL_SUCCESS.includes(s) || TERMINAL_FAILURE.includes(s);
};

const extractFileMeta = (data, fallback = {}) => {
  const fileName = data?.fileName || fallback.fileName;
  let size = fallback.size ?? "—";
  if (data?.fileSizeMB != null) size = `${data.fileSizeMB} MB`;
  else if (data?.fileSizeBytes != null)
    size = `${(data.fileSizeBytes / (1024 * 1024)).toFixed(2)} MB`;
  return { fileName, size };
};

const parseServerDate = (value, fallback = 0) => {
  if (value == null) return fallback;
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "string") {
    const native = Date.parse(value);
    if (!Number.isNaN(native)) return native;
    const parsed = moment(value, "DD MMM YYYY hh:mm A", true);
    if (parsed.isValid()) return parsed.valueOf();
  }
  return fallback;
};

/**
 * Compute progress % from processedJobs / totalJobs.
 * Returns 100 for terminal states so the bar looks full.
 */
const computeProgress = ({
  uiStatus,
  total,
  processed,
}) => {
  if (uiStatus === "ready" || uiStatus === "failed") return 100;
  if (!total || total <= 0) return 5;
  const pct = Math.round((processed / total) * 100);
  // never let active items hit 100 — cap at 99 until terminal
  return Math.max(5, Math.min(99, pct));
};

const mapExportToQueueItem = (e) => {

  const payload =
    e.data && typeof e.data === "object" && e.data.exportID ? e.data : e;

  if (!payload?.exportID) return null;

  const backendStatus = payload.status;
  const total = Number(payload.totalJobs ?? 0);
  const processed = Number(payload.processedJobs ?? 0);
  const successful = Number(payload.successfulJobs ?? 0);
  const failed = Number(payload.failedJobs ?? 0);
  // console.log(payload,"payloadpayloadpayloadpayload")
  const uiStatus = mapBackendStatus(backendStatus, { successful, failed });

  const { fileName, size } = extractFileMeta(payload, {
    fileName: `work-order-reports_${String(payload.exportID).slice(0, 8)}.zip`,
    size: "—",
  });

  const progress = computeProgress({ uiStatus, total, processed });

  return {
    id: payload.exportID,
    exportId: payload.exportID,
    jobId: Array.isArray(payload.jobIDs)
      ? payload.jobIDs.join(", ")
      : payload.jobIDs || "",
    jobCount: total,
    totalJobs: total,
    processedJobs: processed,
    successfulJobs: successful,
    failedJobs: failed,
    fileName,
    status: uiStatus,
    progress,
    speed:
      uiStatus === "ready"
        ? failed > 0
          ? `${successful} OK, ${failed} failed`
          : `${successful || total}/${total} reports ready`
        : uiStatus === "failed"
          ? "Failed"
          : processed > 0
            ? `Processing ${processed}/${total}`
            : "Queued on server…",
    size,
    downloadUrl: payload.downloadUrl || null,
    jobIDs: e.jobIDs,
    createdAt: parseServerDate(payload.createdAt, Date.now()),
  };
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════
function JobsListing() {
  const location = useLocation();
  const defaultStatusFromDashboard = location?.state?.defaultStatus || null;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);

  const jobListData = useSelector((state) => state.job.jobList);
  const regionListData = useSelector((state) => state.technicians.regionList);
  const jobTypeListData = useSelector((state) => state.jobType.jobTypeList);
  const paymentTermListData = useSelector(
    (state) => state.customer.paymentTermList
  );
  const technicianListData = useSelector(
    (state) => state.technicians.techniciansList
  );
  const customerListData = useSelector((state) => state.customer.customerList);

  const [pageNum, setPageNum] = useState(1);

  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [showDownloadMode, setShowDownloadMode] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [isDownloadingQuotationItemsCSV, setIsDownloadingQuotationItemsCSV] = useState(false);

  const [manualJobIdsInput, setManualJobIdsInput] = useState("");

  const [showDownloadPanel, setShowDownloadPanel] = useState(false);
  const [downloadQueue, setDownloadQueue] = useState([]);
  const exportPollersRef = useRef({});

  const [creationDateRange, setCreationDateRange] = useState([null, null]);
  const [scheduleDateRange, setScheduleDateRange] = useState([null, null]);
  const [jobListState, setJobListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnSearch: false,
  });

  const initialStatusOption = defaultStatusFromDashboard
    ? JOB_STATUS_OPTIONS.find(
      (opt) => opt.value === defaultStatusFromDashboard
    ) || null
    : null;

  const [searchState, setSearchState] = useState({
    searchStrings: "",
    appliedSearchText: "",
    jobNo: "",
    status: initialStatusOption,
    region: null,
    jobType: null,
    paymentTerm: null,
    technician: null,
    customer: null,
    internalTrackingFlag: null,
    filterParams: initialStatusOption
      ? JSON.stringify([
        {
          field: "JobStatus",
          condition: "eq",
          filterValues: [initialStatusOption.value],
        },
      ])
      : [],
    isSearched: !!initialStatusOption,
  });

  const [isDownloading, setIsDownloading] = useState(false);

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current?.notificationAlert(options);
  };

  // ════════════════════════════════════════════════════════════════
  // JOB LIST
  // ════════════════════════════════════════════════════════════════
  useEffect(() => {
    getJobList(dispatch, {
      pageNum: pageNum,
      filters: searchState.filterParams,
      searchString: searchState.searchStrings,
    });
    return () => {
      dispatch({ type: jobList, payload: {} });
    };
  }, [dispatch, pageNum, searchState.filterParams]);

  useEffect(() => {
    if (jobListData?.code === 200) {
      setJobListState({
        isLoading: false,
        error: "",
        noRecords: jobListData?.data?.job?.length === 0,
        showLoaderOnSearch: false,
      });
    } else if (jobListData?.code || jobListData?.message) {
      setJobListState({
        isLoading: false,
        error:
          jobListData?.message || "Something went Wrong! Please try again.",
        noRecords: false,
        showLoaderOnSearch: false,
      });
    }
  }, [jobListData]);

  useEffect(() => {
    const filterJSON = [
      { field: "UserStatus", condition: "eq", filterValues: ["ACTIVE"] },
    ];
    const statusFilterJSON = [
      { field: "Status", condition: "eq", filterValues: ["ACTIVE"] },
    ];
    getRegionList(dispatch);
    getPaymentTermList(dispatch);
    getJobTypeList(dispatch, { filters: JSON.stringify(statusFilterJSON) });
    getTechniciansList(dispatch, { filters: JSON.stringify(filterJSON) });
    getCustomerList(dispatch, { filters: JSON.stringify(filterJSON) });
    return () => {
      dispatch({ type: regionList, payload: {} });
      dispatch({ type: paymentTermList, payload: {} });
      dispatch({ type: jobTypeList, payload: {} });
      dispatch({ type: techniciansList, payload: {} });
      dispatch({ type: customerList, payload: {} });
    };
  }, [dispatch]);

  useEffect(() => {
    if (defaultStatusFromDashboard) {
      setPageNum(1);
      setJobListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnSearch: true,
      }));
    }
  }, [defaultStatusFromDashboard]);

  // ════════════════════════════════════════════════════════════════
  // ROW / DATE HANDLERS
  // ════════════════════════════════════════════════════════════════
  const handleRowOnClick = (rowData) => {
    if (showDownloadMode) return;
    const encodedId = urlEncodedId(rowData.ID);
    window.open(
      `/admin/jobs/${encodedId}/details`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleCreationChange = (dates) => setCreationDateRange(dates);
  const handleScheduleChange = (dates) => setScheduleDateRange(dates);

  const [creationStart, creationEnd] = creationDateRange;
  const [scheduleStart, scheduleEnd] = scheduleDateRange;

  // ════════════════════════════════════════════════════════════════
  // SELECTION
  // ════════════════════════════════════════════════════════════════
  const handleRowSelection = (e, row) => {
    e.stopPropagation();
    const jobId = row?.ID;
    setSelectedJobIds((prev) =>
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      setSelectedJobIds(formattedJobList.map((item) => item.ID));
    } else {
      setSelectedJobIds([]);
    }
  };

  const handleDownloadModeToggle = () => {
    setShowDownloadMode((prev) => !prev);
    if (showDownloadMode) {
      setSelectedJobIds([]);
      setManualJobIdsInput("");
    }
  };

  const handleDownloadBulkOrderReport = () => handleDownloadModeToggle();

  // ════════════════════════════════════════════════════════════════
  // MANUAL INPUT
  // ════════════════════════════════════════════════════════════════
  const parseAndCapManualJobIds = (input) => {
    if (!input) return { ids: [], cappedInput: "" };
    const seen = new Set();
    const ids = [];
    const acceptedTokens = [];
    input.split(",").forEach((token) => {
      if (ids.length >= MAX_MANUAL_JOB_IDS) return;
      const cleaned = token.trim();
      if (cleaned === "" || !/^\d+$/.test(cleaned)) return;
      const num = Number(cleaned);
      if (Number.isInteger(num) && num > 0 && !seen.has(num)) {
        seen.add(num);
        ids.push(num);
        acceptedTokens.push(cleaned);
      }
    });
    return { ids, cappedInput: acceptedTokens.join(", ") };
  };

  const { ids: manualJobIds } = useMemo(
    () => parseAndCapManualJobIds(manualJobIdsInput),
    [manualJobIdsInput]
  );

  const hasInvalidManualTokens = useMemo(() => {
    if (!manualJobIdsInput) return false;
    return manualJobIdsInput
      .split(",")
      .some((t) => t.trim() !== "" && !/^\d+$/.test(t.trim()));
  }, [manualJobIdsInput]);

  const allSelectedJobIds = useMemo(() => {
    const set = new Set([...selectedJobIds, ...manualJobIds]);
    return Array.from(set);
  }, [selectedJobIds, manualJobIds]);

  const formattedJobList = jobListData?.data?.job?.map((item, index) => ({
    ...item,
    srNo:
      (pageNum - 1) *
      (jobListData?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
      index +
      1,
    selected:
      selectedJobIds.includes(item.ID) || manualJobIds.includes(item.ID),
    selectedFromInput: manualJobIds.includes(item.ID),
  }));

  // ════════════════════════════════════════════════════════════════
  // POLLING
  // ════════════════════════════════════════════════════════════════
  const stopExportPolling = (exportId) => {
    const t = exportPollersRef.current[exportId];
    if (t) {
      clearInterval(t);
      delete exportPollersRef.current[exportId];
    }
  };

  const startExportPolling = (exportId) => {
    stopExportPolling(exportId);

    const tick = async () => {
      try {
        const raw = await downloadBulkWorkOrderReportsStatus(exportId);
        const payload = unwrapExportPayload(raw);

        const backendStatus = payload.status;
        const total = Number(payload.totalJobs ?? 0);
        const processed = Number(payload.processedJobs ?? 0);
        const successful = Number(payload.successfulJobs ?? 0);
        const failed = Number(payload.failedJobs ?? 0);

        const uiStatus = mapBackendStatus(backendStatus, {
          successful,
          failed,
        });

        // ✅ Terminal once COMPLETED / PROCESSED / FAILED / CANCELLED
        const terminal = isTerminalStatus(backendStatus);

        setDownloadQueue((prev) =>
          prev.map((d) => {
            if (d.id !== exportId) return d;

            const { fileName, size } = extractFileMeta(payload, d);

            const progress = computeProgress({
              uiStatus,
              total,
              processed,
            });

            let speed = d.speed;
            if (uiStatus === "downloading") {
              speed =
                processed > 0
                  ? `Processing ${processed}/${total}`
                  : "Queued on server…";
            } else if (uiStatus === "ready") {
              speed =
                failed > 0
                  ? `${successful} OK, ${failed} failed`
                  : `${successful || total}/${total} reports ready`;
            } else if (uiStatus === "failed") {
              speed = "Failed";
            }

            return {
              ...d,
              status: uiStatus,
              progress,
              speed,
              fileName,
              size,
              totalJobs: total,
              processedJobs: processed,
              successfulJobs: successful,
              failedJobs: failed,
            };
          })
        );

        // ✅ Stop polling on terminal status
        if (terminal) {
          stopExportPolling(exportId);
        }
      } catch (err) {
        console.error("Poll export status failed:", err);
      }
    };

    tick();
    const intervalId = setInterval(tick, 3000);
    exportPollersRef.current[exportId] = intervalId;
  };

  // ════════════════════════════════════════════════════════════════
  // SUBMIT + DOWNLOAD
  // ════════════════════════════════════════════════════════════════
  const handleDownloadSelectedReports = async () => {
    const jobIdsToDownload = allSelectedJobIds;

    if (!jobIdsToDownload?.length) {
      notify("danger", "Please select or enter at least one Work Order ID.");
      return;
    }

    const payload = { jobIDs: jobIdsToDownload.map(Number) };

    try {
      setIsDownloadingReport(true);

      const raw = await downloadBulkWorkOrderReport(payload);
      const data = unwrapExportPayload(raw);

      const exportId = data?.exportID;
      if (!exportId) {

        if (data.data?.missing_job_ids.length > 0) {
          // ✅ Remove duplicates (e.g. [33, 44, 33, 55] → [33, 44, 55])
          const missingIds = [...new Set(data.data.missing_job_ids.map(String))];

          const formattedIds = missingIds.map((id) => `• ${id}`).join("\n");

          showAlert({
            title: "⚠️ Work Order ID Not Found",
            message: `The following Work Order ID${missingIds.length > 1 ? "s do" : " does"
              } not exist:\n\n${formattedIds}\n\nDo you want to remove ${missingIds.length > 1 ? "these IDs" : "this ID"
              } from the input?`,
            type: "warning",
            confirmStyle: "danger",
            cancelStyle: "secondary",
            confirmText: "Yes, Remove",
            cancelText: "Cancel",

            // ✅ Remove missing IDs from the textarea
            onConfirm: () => {
              setManualJobIdsInput((prev) => {
                if (!prev) return prev;

                const missingSet = new Set(missingIds);

                const remaining = prev
                  .split(",")
                  .map((token) => token.trim())
                  .filter((token) => token !== "" && !missingSet.has(token));

                return remaining.join(", ");
              });

              // Also drop them from the selected checkbox list
              setSelectedJobIds((prev) =>
                prev.filter((id) => !missingIds.includes(String(id)))
              );

              notify(
                "success",
                `Removed ${missingIds.length} invalid Work Order ID${missingIds.length > 1 ? "s" : ""
                } from the list.`
              );
            },

            onCancel: () => { },
          });
        }

        throw new Error(
          raw?.message ||
          data?.message ||
          "No export ID returned from server"
        );
      }

      const totalJobs = Number(data?.totalJobs ?? jobIdsToDownload.length);

      notify(
        "success",
        `Export started for ${totalJobs} work order(s). Track progress in the Downloads panel.`
      );

      const queueItem = {
        id: exportId,
        exportId,
        jobId: jobIdsToDownload.join(", "),
        jobCount: totalJobs,
        totalJobs,
        processedJobs: 0,
        successfulJobs: 0,
        failedJobs: 0,
        fileName: `work-order-reports_${exportId.slice(0, 8)}.zip`,
        status: "downloading",
        progress: 5,
        speed: "Queued on server…",
        size: "—",
        downloadUrl: null,
        createdAt: Date.now(),
      };

      setDownloadQueue((prev) => [queueItem, ...prev]);
      setShowDownloadPanel(true);
      startExportPolling(exportId);

      setSelectedJobIds([]);
      setManualJobIdsInput("");
      setShowDownloadMode(false);
    } catch (error) {


      notify(
        "danger",
        error?.message || "Something went wrong while starting the report"
      );
    } finally {
      setIsDownloadingReport(false);
    }
  };

 // ════════════════════════
// DOWNLOAD WORK ORDER QUOTATION ITEMS CSV (direct blob download)
// ════════════════════════
const handleDownloadQuotationItemsCSV = async () => {
  const jobIdsToDownload = allSelectedJobIds;

  if (!jobIdsToDownload?.length) {
    notify("danger", "Please select or enter at least one Work Order ID.");
    return;
  }

  const payloadJobIds = jobIdsToDownload.map(Number);

  try {
    setIsDownloadingQuotationItemsCSV(true);

    // ✅ send payload as { jobIDs: [...] }
    const raw = await downloadWorkOrderQuotationItemsCSV({
      jobIDs: payloadJobIds,
    });

    const blob =
      raw?.data instanceof Blob
        ? raw.data
        : new Blob([raw?.data ?? raw], { type: "text/csv;charset=utf-8;" });

    const contentType = raw?.headers?.["content-type"] || blob.type;
    if (contentType.includes("json")) {
      const errorPayload = JSON.parse(await blob.text());
      throw new Error(
        errorPayload?.message ||
          "Failed to download Work Order CSV, try again later"
      );
    }

    const fileName = `work-order-quotation-items_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }, 500);

    notify("success", "CSV downloaded successfully");

    setSelectedJobIds([]);
    setManualJobIdsInput("");
  } catch (error) {
    notify(
      "danger",
      error?.message || "Something went wrong while downloading the CSV"
    );
  } finally {
    setIsDownloadingQuotationItemsCSV(false);
    setShowDownloadMode(false)
  }
};

  const handleDownloadReport = async (queueItem) => {
    const key = queueItem.exportId || queueItem.id;

    try {
      setDownloadQueue((prev) =>
        prev.map((d) => (d.id === key ? { ...d, speed: "Fetching URL…" } : d))
      );

      const raw = await downloadBulkWorkOrderReportsdownloadByBrowser(key);
      const url =
        (typeof raw === "string" && raw) ||
        raw?.data?.downloadURL ||
        raw?.data?.downloadUrl ||
        raw?.data?.url ||
        raw?.data?.s3Url ||
        raw?.data?.signedUrl ||
        raw?.downloadURL ||
        raw?.downloadUrl ||
        raw?.url ||
        raw?.s3Url ||
        raw?.signedUrl ||
        (typeof raw?.data === "string" && raw.data) ||
        null;

      if (!url) {
        console.error("No URL found in response:", raw);
        throw new Error("Download URL not returned by server");
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = queueItem.fileName || "work-order-reports.zip";
      link.rel = "noopener,noreferrer";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 500);

      setDownloadQueue((prev) =>
        prev.map((d) =>
          d.id === key
            ? { ...d, status: "completed", progress: 100, speed: "Downloaded" }
            : d
        )
      );

      notify("success", "Download started");
    } catch (err) {
      console.error("Download failed:", err);
      notify("danger", err?.message || "Failed to download report");
      setDownloadQueue((prev) =>
        prev.map((d) =>
          d.id === key ? { ...d, status: "failed", speed: "Failed" } : d
        )
      );
    }
  };

  // ════════════════════════════════════════════════════════════════
  // QUEUE ACTIONS
  // ════════════════════════════════════════════════════════════════
  const handleCancelDownload = (id) => {
    stopExportPolling(id);
    setDownloadQueue((prev) => prev.filter((d) => d.id !== id));
  };

  const handleCancelAll = () => {
    Object.values(exportPollersRef.current).forEach(clearInterval);
    exportPollersRef.current = {};
    setDownloadQueue([]);
  };

  const handleClearCompleted = () => {
    setDownloadQueue((prev) =>
      prev.filter(
        (d) => !["completed", "cancelled", "failed"].includes(d.status)
      )
    );
  };

  const handleRetryDownload = (id) => {
    setDownloadQueue((prev) =>
      prev.map((d) =>
        d.id === id
          ? { ...d, status: "downloading", progress: 5, speed: "Retrying…" }
          : d
      )
    );
    startExportPolling(id);
  };

  // ════════════════════════════════════════════════════════════════
  // HYDRATE FROM SERVER — persistent list across refreshes
  // ════════════════════════════════════════════════════════════════
  const hydrateExportList = async () => {
    try {
      const raw = await downloadBulkWorkOrderReportList();
      const list = extractExportsArray(raw);

      const items = list.map(mapExportToQueueItem).filter(Boolean);

      setDownloadQueue((prev) => {
        const liveIds = new Set(Object.keys(exportPollersRef.current));
        const map = new Map(prev.map((p) => [p.id, p]));

        items.forEach((i) => {
          if (liveIds.has(i.id)) {
            // Merge — keep the live progress for still-running items
            const live = map.get(i.id);
            map.set(i.id, {
              ...i,
              progress:
                live?.status === "downloading"
                  ? Math.max(live.progress, i.progress)
                  : i.progress,
              status:
                live?.status === "ready" || live?.status === "completed"
                  ? live.status
                  : i.status,
            });
          } else if (!map.has(i.id)) {
            map.set(i.id, i);
          } else {
            map.set(i.id, { ...map.get(i.id), ...i });
          }
        });

        return Array.from(map.values()).sort(
          (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
        );
      });

      // Only poll items that are NOT already in a terminal state
      items
        .filter((i) => i.status === "downloading")
        .forEach((i) => {
          if (!exportPollersRef.current[i.id]) startExportPolling(i.id);
        });
    } catch (err) {
      console.error("Failed to load export list:", err);
    }
  };

  useEffect(() => {
    hydrateExportList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showDownloadPanel) hydrateExportList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDownloadPanel]);

  // Cleanup pollers on unmount
  useEffect(() => {
    return () => {
      Object.values(exportPollersRef.current).forEach(clearInterval);
      exportPollersRef.current = {};
    };
  }, []);

  // ════════════════════════════════════════════════════════════════
  // COLUMNS
  // ════════════════════════════════════════════════════════════════
const columns = [
{
  Header: () => (
    <div className="checkbox-cell-inner">
      {showDownloadMode && (
        <Input
          type="checkbox"
          checked={
            formattedJobList?.length > 0 &&
            formattedJobList.every((item) => item.selected)
          }
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="job-checkbox-all"
        />
      )}
    </div>
  ),
  accessor: "selected",
  id: "checkbox-select",
  className: "col-checkbox",
  width: 50,
  minWidth: 50,
  maxWidth: 50,
  disableSortBy: true,
  Cell: ({ row }) => {
    if (!showDownloadMode) return null;
    return (
      <div className="checkbox-cell-inner">
        <Input
          type="checkbox"
          checked={row.original.selected}
          onChange={(e) => handleRowSelection(e, row.original)}
          className="job-checkbox"
        />
      </div>
    );
  },
},
  {
    Header: `${WORK_ORDER} No.`,
    accessor: "ID",
    className: "col-job-no",
    width: 130,
    minWidth: 130,
    Cell: ({ value }) => <span>{value || "-"}</span>,
  },
  {
    Header: "Customer Info",
    accessor: "customer",
    className: "col-job-cust-info",
    width: 170,
    minWidth: 170,
    Cell: ({ row }) => {
      const { customer, customerLocation } = row?.original;
      return (
        <div>
          <div className="d-flex align-items-center mb-1">
            <i className="fa fa-user mr-2 text-light" />
            <span>{customer?.companyName || "-"}</span>
          </div>
          <div className="d-flex align-items-center">
            <i className="fa fa-building mr-2 text-light" />
            <span>{customerLocation?.outletName || "-"}</span>
          </div>
        </div>
      );
    },
  },
  {
    Header: "Type",
    accessor: "jobTypeID",
    className: "col-job-type ",
    width: 130,
    minWidth: 130,
  
    Cell: ({ value }) => (
      <div className="col-job-type-cell">{value?.name || "-"}</div>
    ),
  },
  {
    Header: "Contact Person Info",
    accessor: "customerLocation",
    className: "col-job-cont-info",
    width: 160,
    minWidth: 160,
    Cell: ({ value }) => {
      const name = value?.contactPersonName || "-";
      const phone = value?.contactPersonPhone || "-";
      return (
        <div>
          <div className="d-flex align-items-center mb-1">
            <i className="fa fa-user mr-2 text-light" />
            <span>{name}</span>
          </div>
          <div className="d-flex align-items-center">
            <i className="fa fa-phone mr-2 text-light" />
            <span>+966 {phone}</span>
          </div>
        </div>
      );
    },
  },
  {
    Header: "Payment Term",
    accessor: "paymentTerm.term",
    className: "col-payment-term",
    width: 110,
    minWidth: 110,
    Cell: ({ value }) => <div>{value || "-"}</div>,
  },
  {
    Header: "Amount (SAR)",
    accessor: "paymentTerm.amount",
    className: "col-job-amount",
    width: 110,
    minWidth: 110,
    Cell: ({ value }) => <div>{value ?? 0}</div>,
  },
  {
    Header: "Region",
    id: "region",
    accessor: (row) => row.customerLocation?.regionResponce?.name,
    className: "col-job-region",
    width: 90,
    minWidth: 90,
    Cell: ({ value }) => <span>{value || "-"}</span>,
  },
  {
    Header: "Assigned To",
    accessor: "technician",
    className: "col-assign-to",
    width: 140,
    minWidth: 140,
    Cell: ({ value }) => <span>{value?.name || "-"}</span>,
  },
  {
    Header: "Schedule Date",
    accessor: "scheduledFrom",
    className: "col-scheduled-from",
    width: 170,
    minWidth: 170,
    Cell: ({ row }) => {
      const { scheduledFrom, scheduledTo } = row?.original;
      return (
        <div className="d-flex flex-column align-items-center mb-1">
          <span className="text-nowrap">
            {ISOToHumanReadableDate(scheduledFrom)}
          </span>
          <strong className="my-1">To</strong>
          <span className="text-nowrap">
            {ISOToHumanReadableDate(scheduledTo)}
          </span>
        </div>
      );
    },
  },
  {
    Header: "Status",
    accessor: "jobStatus",
    className: "col-job-status",
    width: 190,
    minWidth: 190,
   Cell: ({ value }) => {
  const displayValue = 
    value === "JOB_ALLOCATED" ? "WORK_ORDER_ALLOCATED" : 
    value === "JOB_REJECTED_BY_CLIENT" ? "WORK_ORDER_REJECTED_BY_CLIENT" : 
    value;

  return (
    <Badge color={"secondary"} pill>
      {displayValue || "-"}
    </Badge>
  );
},
  },
  {
    Header: "Quotation Status",
    accessor: "quotationStatus",
    className: "col-quotation-status",
    width: 190,
    minWidth: 190,
    Cell: ({ value }) => (
      <Badge  pill>
        {value }
      </Badge> || "-"
    ),
  },
  {
    Header: "Parts Order Status",
    accessor: "partRequestedStatus",
    className: "col-parts-status",
    width: 150,
    minWidth: 150,
    Cell: ({ value }) => <span>{value || "-"}</span>,
  },
  {
    Header: "Sales Reference",
    accessor: "jobSalesOrderNo",
    className: "col-salesorderno",
    width: 130,
    minWidth: 130,
    Cell: ({ value }) => <span>{value || "-"}</span>,
  },
  {
    Header: "Creation Date",
    accessor: "createdAt",
    className: "col-created-at",
    width: 130,
    minWidth: 130,
    Cell: ({ value }) =>
      <span>{value ? ISOToHumanReadableDate(value) : "-"}</span>,
  },
];

    const handleSearch = () => {
      const newfilterParams = buildFilterParams();
      const trimmedSearch = searchState.searchStrings.trim();

      // Compare against the last APPLIED search text, not the live input,
      // so pressing Enter with unchanged text doesn't re-fetch forever,
      // while clearing/typing new text DOES trigger a refetch.
      const appliedSearchText = searchState.appliedSearchText || "";
      const textChanged = trimmedSearch !== appliedSearchText;
      const filtersChanged =
        JSON.stringify(searchState.filterParams) !==
        JSON.stringify(newfilterParams);

      if (!textChanged && !filtersChanged) return;

      setSearchState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
        appliedSearchText: trimmedSearch,
        searchStrings: trimmedSearch,
        isSearched: true,
      }));
      setJobListState((prev) => ({
        ...prev,
        showLoaderOnSearch: true,
        isLoading: true,
      }));
      if (pageNum !== 1) setPageNum(1);
    };

  // ════════════════════════════════════════════════════════════════
  // SEARCH
  // ════════════════════════════════════════════════════════════════
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

    const buildFilterParams = () => {
    const filterParams = [];

    if (searchState?.jobNo) {
      filterParams.push({
        field: "ID",
        condition: "eq",
        filterValues: [String(searchState.jobNo)],
      });
    }

    if (searchState?.region?.ID)
      filterParams.push({
        field: "CustomerRegionID",
        condition: "eq",
        filterValues: [String(searchState.region.ID)],
      });

    if (searchState?.paymentTerm?.ID) {
      filterParams.push({
        field: "PaymentTermID",
        condition: "eq",
        filterValues: [String(searchState.paymentTerm.ID)],
      });
    }

    if (searchState?.jobType?.ID) {
      filterParams.push({
        field: "JobTypeID",
        condition: "eq",
        filterValues: [String(searchState.jobType.ID)],
      });
    }

    if (searchState?.status?.value)
      filterParams.push({
        field: "JobStatus",
        condition: "eq",
        filterValues: [searchState.status.value],
      });

    if (searchState?.quotationStatus?.value)
      filterParams.push({
        field: "QuotationStatus",
        condition: "eq",
        filterValues: [searchState.quotationStatus.value],
      });

    if (scheduleStart && scheduleEnd) {
      filterParams.push({
        field: "ScheduledTo",
        condition: "btw",
        filterValues: [
          formatDateLocal(scheduleStart),
          formatDateLocal(scheduleEnd),
        ],
      });
    }

    if (creationStart && creationEnd) {
      filterParams.push({
        field: "CreatedAt",
        condition: "btw",
        filterValues: [
          formatDateLocal(creationStart),
          formatDateLocal(creationEnd),
        ],
      });
    }

    if (searchState?.technician?.ID)
      filterParams.push({
        field: "TechnicianID",
        condition: "eq",
        filterValues: [String(searchState.technician.ID)],
      });

    if (searchState?.customer?.ID)
      filterParams.push({
        field: "CustomerID",
        condition: "eq",
        filterValues: [String(searchState.customer.ID)],
      });


    if (searchState?.internalTrackingFlag)
      filterParams.push({
        field: "InternalTrackingFlag",
        condition: "eq",
        filterValues: [searchState.internalTrackingFlag.value],
      });

    if (scheduleStart && !scheduleEnd) {
      filterParams.push({
        field: "ScheduledTo",
        condition: "btw",
        filterValues: [
          formatDateLocal(scheduleStart),
          formatDateLocal(scheduleStart)
        ],
      });
    }

    if (creationStart && !creationEnd) {
      filterParams.push({
        field: "CreatedAt",
        condition: "btw",
        filterValues: [
          formatDateLocal(creationStart),
          formatDateLocal(creationStart)
        ],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };



    const clearSearch = () => {
      setSearchState({
        searchStrings: "",
        appliedSearchText: "",
        status: null,
        region: null,
        jobType: null,
        jobNo: "",
        technician: null,
        customer: null,
        paymentTerm: null,
        internalTrackingFlag: null,
        filterParams: [],
        isSearched: false,
        quotationStatus: "",
      });
      setCreationDateRange([null, null]);
      setScheduleDateRange([null, null]);
      setJobListState((prev) => ({ ...prev, isLoading: true }));
      if (pageNum !== 1) setPageNum(1);
    };

    const handleExportCsv = async () => {
      try {
        setIsDownloading(true);
        const res = await exportJobsCSV({
          filters: searchState.filterParams,
          searchString: searchState.searchStrings,
        });
        if (res.status === 200) {
          const blob = new Blob(["\uFEFF" + res?.data], {
            type: "text/csv;charset=utf-8;",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `work-order-report_${moment().format("YYYY-MM-DD")}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          notify("success", res?.message || "CSV exported successfully");
        } else {
          const message = Array.isArray(res?.message || res?.data?.message)
            ? res?.message[0]?.Msg || res?.data?.message[0]?.Msg
            : res?.message ||
            res?.data?.message ||
            "Failed to export csv, try again later";
          notify("danger", message);
        }
      } catch (error) {
        notify("danger", error?.message || "Something went wrong");
      } finally {
        setIsDownloading(false);
      }
    };

    // ════════════════════════════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════════════════════════════
    return (
      <>
        {isDownloading && (
          <div className="loading-overlay">
            <div className="loading-card">
              <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
              <div className="font-weight-bold text-dark">
                Processing… Please wait.
              </div>
            </div>
          </div>
        )}
        <div className="rna-container">
          <NotificationAlert ref={notificationAlertRef} />
        </div>

        <Container fluid className="mt-4">
          <Row>
            <Col className="mb-5" md="12">
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between mb-2">
                  <CardTitle tag="h3" className="font-weight-normal">
                    {WORK_ORDER}s
                  </CardTitle>
                  <div className="d-flex align-items-center">
                    {!showDownloadMode ? (
                      <>
                        <Button
                          className="btn-simple btn-round btn-style mr-3"
                          color="default"
                          size="md"
                          onClick={handleExportCsv}
                        >
                          <i className="fas fa-arrow-up-from-bracket mr-1" /> Export{" "}
                          {WORK_ORDER}
                        </Button>
                        <Button
                          className="btn-simple btn-round btn-style mr-3 d-flex align-items-center"
                          color="default"
                          size="md"
                          onClick={handleDownloadBulkOrderReport}
                        >
                          <i className="fa-solid fa-cloud-arrow-down mr-2" />
                          Download Reports / CSV
                          <span
                            className="download-panel-toggle ml-2"
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDownloadPanel(true);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.stopPropagation();
                                setShowDownloadPanel(true);
                              }
                            }}
                            title="Open download panel"
                          >
                            <i className="fa-solid fa-list-check" />
                            {downloadQueue.filter(
                              (d) => d.status === "downloading"
                            ).length > 0 && (
                                <span className="download-panel-badge">
                                  {
                                    downloadQueue.filter(
                                      (d) => d.status === "downloading"
                                    ).length
                                  }
                                </span>
                              )}
                          </span>
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="manual-wo-input-wrapper">
                          <textarea
                            className={`manual-wo-input ${hasInvalidManualTokens ? "has-error" : ""
                              }`}
                            placeholder={`Enter Work Order IDs (comma separated, max ${MAX_MANUAL_JOB_IDS})`}
                            value={manualJobIdsInput}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const sanitized = raw.replace(/[^\d,\s]/g, "");
                              const { cappedInput } =
                                parseAndCapManualJobIds(sanitized);
                              setManualJobIdsInput(
                                parseAndCapManualJobIds(sanitized).ids.length >=
                                  MAX_MANUAL_JOB_IDS
                                  ? cappedInput
                                  : sanitized
                              );
                            }}
                            rows={1}
                            spellCheck={false}
                          />
                        </div>

                        <Button
                          className="btn-simple btn-round btn-style mr-3"
                          color="default"
                          size="md"
                          onClick={handleDownloadSelectedReports}
                          disabled={
                            allSelectedJobIds.length === 0 || isDownloadingReport
                          }
                        >
                          {isDownloadingReport ? (
                            <>
                              <i className="fa fa-spinner fa-spin mr-1" />{" "}
                              Downloading...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-cloud-arrow-down mr-1" />
                              Download Reports ({allSelectedJobIds.length})
                            </>
                          )}
                        </Button>
                        <Button
                          className="btn-simple btn-round btn-style mr-3"
                          color="default"
                          size="md"
                          onClick={handleDownloadQuotationItemsCSV}
                          disabled={
                            allSelectedJobIds.length === 0 ||
                            isDownloadingQuotationItemsCSV ||
                            isDownloadingReport
                          }
                        >
                          {isDownloadingQuotationItemsCSV ? (
                            <>
                              <i className="fa fa-spinner fa-spin mr-1" />{" "}
                              Downloading...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-file-csv mr-1" />
                              Download CSV ({allSelectedJobIds.length})
                            </>
                          )}
                        </Button>
                        <Button
                          className="btn-simple btn-round btn-style mr-3"
                          color="default"
                          size="md"
                          onClick={handleDownloadModeToggle}
                        >
                          <i className="fa-solid fa-xmark mr-1" /> Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </CardHeader>

                <CardBody>
                  <div className="d-flex flex-wrap align-items-center justify-content-start filter-group">
                    <Input
                      type="text"
                      placeholder={`Search by ${SMALL_CASE_WORK_ORDER} no.`}
                      className="mr-2 mb-2 filter-input-small"
                      value={searchState.jobNo}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          jobNo: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />
                    <Input
                      type="text"
                      placeholder="Search by contact name or phone"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.searchStrings}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          searchStrings: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={technicianListData?.data?.technician}
                      getOptionLabel={(option) => option.fullName}
                      getOptionValue={(option) => option.ID}
                      value={searchState.technician}
                      placeholder="Technician"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle(true)}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          technician: {
                            ID: selected.ID,
                            userID: selected.userID,
                            fullName: selected.fullName,
                          },
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={customerListData?.data?.customer}
                      getOptionLabel={(option) =>
                        `${option?.firstName || ""} ${option?.lastName || ""
                          }`.trim() || `${option?.companyName}`.trim()
                      }
                      getOptionValue={(option) => option.ID}
                      value={searchState.customer}
                      placeholder="Customer"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle(true)}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          customer: {
                            ID: selected.ID,
                            userID: selected.userID,
                            firstName: selected?.firstName,
                            lastName: selected?.lastName,
                            companyName:selected?.companyName,
                          },
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={paymentTermListData?.data?.paymentTerm}
                      getOptionLabel={(option) => option.term}
                      getOptionValue={(option) => option.ID}
                      value={searchState.paymentTerm}
                      placeholder="Payment term"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle(true)}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          paymentTerm: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={jobTypeListData?.data?.jobType}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.ID}
                      value={searchState.jobType}
                      placeholder={`${CAP_CASE_WORK_ORDER} type`}
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle(true)}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          jobType: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={regionListData?.data?.region}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.ID}
                      value={searchState.region}
                      placeholder="Region"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle(true)}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          region: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={JOB_STATUS_OPTIONS}
                      value={searchState.status}
                      placeholder="Status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle(true)}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          status: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={INTERNAL_MONITORING_STATUS}
                      value={searchState.internalTrackingFlag}
                      placeholder="Under internal tracking"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle(true)}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          internalTrackingFlag: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Row className="mx-0">
                      <Col
                        className="d-flex align-items-center mb-2 px-1"
                        style={{ flex: 1 }}
                      >
                        <DatePicker
                          selected={creationStart}
                          onChange={handleCreationChange}
                          startDate={creationStart}
                          endDate={creationEnd}
                          selectsRange
                          isClearable
                          placeholderText="Select a creation date range"
                          className="form-control"
                          style={{ minWidth: 0, flex: 1 }}
                          dateFormat="dd-MM-yyyy"
                          onKeyDown={(e) => e.preventDefault()}
                        />
                      </Col>

                      <Col
                        className="d-flex align-items-center mb-2 px-1"
                        style={{ flex: 1 }}
                      >
                        <DatePicker
                          selected={scheduleStart}
                          onChange={handleScheduleChange}
                          startDate={scheduleStart}
                          endDate={scheduleEnd}
                          selectsRange
                          isClearable
                          placeholderText="Select a schedule date range"
                          className="form-control"
                          style={{ minWidth: 0, flex: 1 }}
                          dateFormat="dd-MM-yyyy"
                          onKeyDown={(e) => e.preventDefault()}
                        />
                      </Col>
                      <Select
                        className="mr-2 mb-2 filter-select"
                        options={QUOTATION_STATUS}
                        value={searchState.quotationStatus}
                        placeholder="Quotation Status"
                        classNamePrefix="react-select"
                        styles={fixedWidthCustomStyle(true)}
                        onChange={(selected) =>
                          setSearchState((prev) => ({
                            ...prev,
                            quotationStatus: selected,
                          }))
                        }
                        onKeyDown={handleKeyDown}
                      />
                    </Row>

                    <Button
                      color="default"
                      className="d-flex justify-content-center align-items-center mb-2 mt-0 filter-btn btn-simple"
                      onClick={handleSearch}
                    >
                      {jobListState.showLoaderOnSearch ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" /> Search
                        </>
                      )}
                    </Button>

                    {(searchState.filterParams.length > 0 ||
                      searchState.isSearched) && (
                        <Button
                          color="default"
                          className="d-flex justify-content-center align-items-center mt-0 mb-2 ml-2 filter-btn btn-simple"
                          onClick={clearSearch}
                        >
                          <i className="fa fa-times mr-1" /> Clear
                        </Button>
                      )}
                  </div>

                  <div className="mt-2 mb-3 mx-1">
                    {jobListData?.data?.totalRecords ? (
                      searchState.filterParams &&
                        Object.keys(searchState.filterParams).length > 0 ? (
                        <span className="font-weight-bold">
                          Showing {jobListData?.data?.totalFilteredRecords} of{" "}
                          {jobListData?.data?.totalRecords} {WORK_ORDER}s
                        </span>
                      ) : (
                        <span className="font-weight-bold">
                          Total {WORK_ORDER}s: {jobListData?.data?.totalRecords}
                        </span>
                      )
                    ) : null}
                  </div>

                  {jobListState.isLoading ? (
                    <div className="loaderContainer">
                      <i className="fa fa-spinner fa-spin fa-2xl loader" />
                    </div>
                  ) : jobListState.error ? (
                    <div className="loaderContainer">{jobListState.error}</div>
                  ) : jobListState.noRecords ? (
                    <div className="loaderContainer text-muted">
                      {searchState.isSearched
                        ? "No records found for your search."
                        : `No ${SMALL_CASE_WORK_ORDER} added yet.`}
                    </div>
                  ) : (
                    <div className=" job-table">
                      <CommonTable
                        showPaginationBottom={true}
                        defaultPageSize={DEFAULT_RECORDS_PER_PAGE || 25}
                        activePage={pageNum}
                        data={formattedJobList || []}
                        rowClick={handleRowOnClick}
                        resizable={false}
                        fixedColumnWidths
                        columns={columns}
                        className="-striped -highlight table-hover table-fixed"
                        totalPages={Math.ceil(
                          jobListData?.data?.totalFilteredRecords /
                          jobListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE
                        )}
                        setPageNum={setPageNum}
                        rowClassName={(row) =>
                          row.internalTrackingFlag === "YES"
                            ? "monitoring-job"
                            : ""
                        }
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* DOWNLOAD PANEL                                            */}
        {/* ══════════════════════════════════════════════════════════ */}
        <div
          className={`download-panel-overlay ${showDownloadPanel ? "open" : ""}`}
          onClick={() => setShowDownloadPanel(false)}
        >
          <div className="download-panel" onClick={(e) => e.stopPropagation()}>
            <div className="dp-header">
              <div className="dp-header-left">
                <div className="dp-header-icon">
                  <i className="fa-solid fa-cloud-arrow-down" />
                </div>
                <div>
                  <h5 className="dp-title">Downloads</h5>
                  <p className="dp-subtitle">Manage your work order reports</p>
                </div>
              </div>
              <button
                type="button"
                className="dp-close"
                onClick={() => setShowDownloadPanel(false)}
                aria-label="Close panel"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            {downloadQueue.length > 0 &&
              (() => {
                const total = downloadQueue.length;
                const completed = downloadQueue.filter((d) =>
                  ["completed", "ready"].includes(d.status)
                ).length;
                const active = downloadQueue.filter((d) =>
                  ["downloading", "queued"].includes(d.status)
                ).length;
                const paused = downloadQueue.filter(
                  (d) => d.status === "paused"
                ).length;
                const aggregate =
                  total === 0
                    ? 0
                    : Math.round(
                      downloadQueue.reduce(
                        (sum, d) => sum + (d.progress || 0),
                        0
                      ) / total
                    );

                const R = 22;
                const C = 2 * Math.PI * R;
                const offset = C - (aggregate / 100) * C;

                return (
                  <div className="dp-summary">
                    <div className="dp-ring-wrapper">
                      <svg width="56" height="56" viewBox="0 0 56 56">
                        <circle
                          cx="28"
                          cy="28"
                          r={R}
                          stroke="#eef2f7"
                          strokeWidth="5"
                          fill="none"
                        />
                        <circle
                          cx="28"
                          cy="28"
                          r={R}
                          stroke="url(#dpGrad)"
                          strokeWidth="5"
                          fill="none"
                          strokeDasharray={C}
                          strokeDashoffset={offset}
                          strokeLinecap="round"
                          transform="rotate(-90 28 28)"
                          style={{ transition: "stroke-dashoffset 0.4s ease" }}
                        />
                        <defs>
                          <linearGradient
                            id="dpGrad"
                            x1="0"
                            y1="0"
                            x2="1"
                            y2="1"
                          >
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="100%" stopColor="#10b981" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <span className="dp-ring-value">{aggregate}%</span>
                    </div>

                    <div className="dp-summary-stats">
                      <div className="dp-stat">
                        <span className="dp-stat-dot active" />
                        <div>
                          <span className="dp-stat-value">{active}</span>
                          <span className="dp-stat-label">Active</span>
                        </div>
                      </div>

                      <div className="dp-stat">
                        <span className="dp-stat-dot done" />
                        <div>
                          <span className="dp-stat-value">
                            {completed}/{total}
                          </span>
                          <span className="dp-stat-label">Ready</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}



            <div className="dp-list">
              {downloadQueue.length === 0 ? (
                <div className="dp-empty">
                  <div className="dp-empty-icon">
                    <i className="fa-solid fa-cloud-arrow-down" />
                  </div>
                  <h6 className="dp-empty-title">No downloads yet</h6>
                  <p className="dp-empty-desc">
                    Select work orders from the list and click{" "}
                    <strong>Download Reports</strong> to start.
                  </p>

                  <div className="dp-empty-hint">
                    <div className="dp-empty-hint-row">
                      <span className="dp-empty-hint-num">1</span>
                      <span>Enable download mode</span>
                    </div>
                    <div className="dp-empty-hint-row">
                      <span className="dp-empty-hint-num">2</span>
                      <span>Select rows or type IDs</span>
                    </div>
                    <div className="dp-empty-hint-row">
                      <span className="dp-empty-hint-num">3</span>
                      <span>Track progress here</span>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {(() => {
                    const activeItems = downloadQueue.filter((d) =>
                      ["downloading", "queued", "ready"].includes(d.status)
                    );
                    const pausedItems = downloadQueue.filter(
                      (d) => d.status === "paused"
                    );
                    const finishedItems = downloadQueue.filter((d) =>
                      ["completed", "cancelled", "failed"].includes(d.status)
                    );

                    const renderItem = (d) => {
                      const statusIcon =
                        d.status === "completed"
                          ? "fa-circle-check"
                          : d.status === "ready"
                            ? "fa-circle-down"
                            : d.status === "cancelled"
                              ? "fa-circle-xmark"
                              : d.status === "failed"
                                ? "fa-circle-exclamation"
                                : d.status === "paused"
                                  ? "fa-circle-pause"
                                  : d.status === "queued"
                                    ? "fa-clock"
                                    : "fa-file-zipper";

                      // ✅ Only show a live progress bar while the export is
                      //    being processed. Once ready/completed/failed it's full.
                      const showProgress =
                        d.status === "downloading" || d.status === "queued";

                      return (
                        <div
                          key={d.id}
                          className={`dp-item dp-item-${d.status}`}
                        >
                          <div className="dp-item-top">
                            <div
                              className={`dp-item-icon dp-item-icon-${d.status}`}
                            >
                              <i className={`fa-solid ${statusIcon}`} />
                            </div>

                            <div className="dp-item-body">
                              <div className="dp-item-name">{d.fileName}</div>
                              <div className="dp-item-meta">
                                <span className="dp-item-meta-tag">
                                  {d.totalJobs
                                    ? `${d.totalJobs} WOs`
                                    : `WO #${d.jobId}`}
                                </span>
                                <span className="dp-item-meta-sep">•</span>
                                <span>{d.size}</span>
                                {d.status === "downloading" && (
                                  <>
                                    <span className="dp-item-meta-sep">•</span>
                                    <span className="dp-item-speed">
                                      {d.speed}
                                    </span>
                                  </>
                                )}
                              </div>
                              {d.jobIDs && Array.isArray(d.jobIDs) && d.jobIDs.length > 0 && (
                                <span
                                  className="dp-item-meta-tag dp-wo-tag"
                                  title={`WO numbers: ${d.jobIDs.join(", ")}`}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    maxWidth: "100%",
                                    padding: "3px 5px",
                                    backgroundColor: "#EFF6FF",
                                    // border: "1px solid #cbd5e1",
                                    // borderRadius: "12px",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: "#475569",
                                    verticalAlign: "middle",
                                    marginTop: "0.2rem",
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  <span className="dp-item-meta-tag">WO:</span>
                                  <span
                                    style={{
                                      fontFamily: "'SF Mono', Menlo, Consolas, monospace",
                                      color: "#0363ff",
                                    }}
                                  >
                                    {d.jobIDs.slice(0, 5).join(", ")}
                                  </span>
                                  {d.jobIDs.length > 5 && (
                                    <span
                                      style={{
                                        backgroundColor: "#fef3c7",
                                        color: "#92400e",
                                        padding: "1px 6px",
                                        borderRadius: "4px",
                                        fontSize: "10px",
                                        fontWeight: 700,
                                        marginLeft: "2px",
                                      }}
                                    >
                                      +{d.jobIDs.length - 5} more
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>

                            <div className="dp-item-actions">
                              {d.status === "downloading" && (
                                <button
                                  type="button"
                                  className="dp-icon-btn danger"
                                  onClick={() => handleCancelDownload(d.id)}
                                  title="Cancel"
                                >
                                  <i className="fa-solid fa-xmark" />
                                </button>
                              )}

                              {d.status === "ready" && (
                                <button
                                  type="button"
                                  className="dp-icon-btn primary"
                                  onClick={() => handleDownloadReport(d)}
                                  title="Download"
                                >
                                  <i className="fa-solid fa-download" />
                                </button>
                              )}

                              {(d.status === "failed" ||
                                d.status === "cancelled") && (
                                  <button
                                    type="button"
                                    className="dp-icon-btn"
                                    onClick={() => handleRetryDownload(d.id)}
                                    title="Retry"
                                  >
                                    <i className="fa-solid fa-rotate-right" />
                                  </button>
                                )}
                            </div>
                          </div>

                          {/* ✅ Progress bar only while processing */}
                          {showProgress && (
                            <div className="dp-item-progress">
                              <div className="dp-item-progress-track">
                                <div
                                  className={`dp-item-progress-fill dp-item-progress-fill-${d.status}`}
                                  style={{ width: `${d.progress}%` }}
                                />
                              </div>
                              <span className="dp-item-progress-pct">
                                {Math.round(d.progress)}%
                              </span>
                            </div>
                          )}

                          {d.status === "ready" && (
                            <div className="dp-item-ready-bar">
                              <i className="fa-solid fa-check" />
                              <span>
                                Ready — {d.successfulJobs || d.totalJobs}/
                                {d.totalJobs || d.successfulJobs} reports
                                {d.failedJobs > 0
                                  ? ` (${d.failedJobs} failed)`
                                  : ""}
                              </span>
                            </div>
                          )}

                          {d.status === "completed" && (
                            <div className="dp-item-done-bar">
                              <i className="fa-solid fa-check" />
                              <span>Download complete</span>
                            </div>
                          )}

                          {d.status === "failed" && (
                            <div className="dp-item-failed-bar">
                              <i className="fa-solid fa-triangle-exclamation" />
                              <span>Export failed</span>
                            </div>
                          )}
                        </div>
                      );
                    };

                    return (
                      <>
                        {activeItems.length > 0 && (
                          <div className="dp-group">
                            <div className="dp-group-header">
                              <span>Finished</span>
                              <span className="dp-group-count">
                                {activeItems.length}
                              </span>
                            </div>
                            {activeItems.map(renderItem)}
                          </div>
                        )}


                        {finishedItems.length > 0 && (
                          <div className="dp-group">
                            <div className="dp-group-header">
                              <span>Finished</span>
                              <span className="dp-group-count">
                                {finishedItems.length}
                              </span>
                            </div>
                            {finishedItems.map(renderItem)}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }


export default JobsListing;
